import { ref } from 'vue';
import { debounce } from '../../../../core/utils/lodash';
import type { Item, Props } from '../types';

export type API = ReturnType<typeof useAPI>;

export const useAPI = (api: Props['api'], apiSetSearchParams: Props['apiSetSearchParams'], minLength: number, useCache: Props['useCache']) => {
	/**
	 * Список, полученный через API
	 */
	const items = ref<Item[]>([]);

	/**
	 * Кэш для полученных ответов через apiRequest
	 */
	const cache = new Map<string, any>();

	/**
	 * Флаг - идет загрузка
	 */
	const isLoading = ref(false);

	let searchText = '';
	let nextOffset: number | null | undefined;

	if (api && !api.params.limit) {
		api.params.limit = 100;
	}

	/**
	 * Выполнить обращение к API
	 *
	 * При ошибке вернет undefined
	 */
	const callAPI = async (): Promise<Api.ResponseSuccess<any, 'get'> | undefined> => {
		if (!api) return;

		let cacheKey: string | undefined = undefined;

		if (useCache) {
			cacheKey = JSON.stringify(api.params);
			const res = cache.get(cacheKey);

			if (res) {
				return res;
			}
		}

		isLoading.value = true;
		const res = await api.call();
		isLoading.value = false;

		if (res.errors) return;

		if (!Array.isArray(res.result)) {
			console.warn('Array expected in `res.result`');

			return;
		}

		const indexWithError = (res.result as Array<Item | any>).findIndex(item => item.id === undefined || item.name === undefined);
		if (indexWithError !== -1) {
			console.warn(`В result[${indexWithError}] нет id или name`);

			return;
		}

		if (cacheKey) {
			cache.set(cacheKey, res);
		}

		return res as Api.ResponseSuccess<any, 'get'>;
	};

	/**
	 * Загрузить items
	 */
	const load = async (append: boolean) => {
		if (!api) return;

		if (append) {
			// данных о следующих страницах не обнаружено
			if (!nextOffset) return;

			// дозагружать нельзя, если не завершена предыдущая загрузка
			if (isLoading.value) return;

			api.params.offset = nextOffset;
		} else {
			api.params.offset = 0;
		}

		apiSetSearchParams?.(searchText);

		const res = await callAPI();
		if (!res) return;

		nextOffset = res.nextOffset;

		if (append) {
			items.value = items.value.concat(res.result);
		} else {
			items.value = res.result;
		}
	};

	const loadDebounce = debounce(() => load(false), 200);

	/**
	 * Выполнить поиск по указанному тексту
	 *
	 * Если длина текста меньше minLength, поиск не будет произведен
	 *
	 * Если текст не изменился, поиск не будет произведен
	 *
	 * @param newSearchText - текст поиска
	 * @param useDebounce - дедупликация загрузки списка
	 */
	const setSearchTextAndLoad = (newSearchText: string, useDebounce = true) => {
		if (!api) return;

		if (newSearchText.length < minLength) return;

		// условия поиска не поменялись, данные загружены
		if (newSearchText === searchText && items.value.length) return;

		searchText = newSearchText;

		if (useDebounce) {
			void loadDebounce();
		} else {
			void load(false);
		}
	};

	return {
		apiRequest: api,
		items,
		isLoading,
		load,
		setSearchTextAndLoad,
	};
};
